import { useState, useMemo, useCallback } from "react";
import { collectImageUrls, pickHeroUrl } from "../utils/image";

/**
 * Runtime image-fallback hook for DealCard-style components.
 *
 * Returns `{ currentUrl, onError }`. `currentUrl` is the first
 * candidate URL that has not yet errored at runtime:
 *   1. The hero URL from `pickHeroUrl(listing)` (preserves existing
 *      selection logic — `image_health` gating in `utils/image.js`
 *      is NOT touched).
 *   2. The remaining URLs from `collectImageUrls(listing)`, in
 *      primary-then-images[] order.
 *
 * When the rendered `<img>` fires `onError`, the offending URL is
 * marked failed and the next candidate becomes `currentUrl` on the
 * next render. When all candidates have failed, `currentUrl` is
 * `null` and the caller should render its existing placeholder.
 *
 * The failed-set resets whenever `listing.id` changes (React 18+
 * `state-derived-from-prop` pattern — avoids `setState-in-effect`
 * by comparing the previous id during render).
 *
 * Phase H2-followup round-8 — Image-Reliability P1 (frontend-only
 * fallback layer on top of unchanged `pickHeroUrl`).
 */
export function useImageFallback(listing) {
  const listingId = listing?.id ?? null;
  const [trackedId, setTrackedId] = useState(listingId);
  const [failedUrls, setFailedUrls] = useState(() => new Set());

  if (trackedId !== listingId) {
    setTrackedId(listingId);
    setFailedUrls(new Set());
  }

  const candidates = useMemo(() => {
    const all = collectImageUrls(listing);
    const hero = pickHeroUrl(listing);
    if (!hero) return all;
    return [hero, ...all.filter((u) => u !== hero)];
  }, [listing]);

  const currentUrl =
    candidates.find((u) => !failedUrls.has(u)) ?? null;

  const onError = useCallback(() => {
    if (!currentUrl) return;
    setFailedUrls((prev) => {
      if (prev.has(currentUrl)) return prev;
      const next = new Set(prev);
      next.add(currentUrl);
      return next;
    });
  }, [currentUrl]);

  return { currentUrl, onError };
}
